import {
  formatChangeDate,
  formatChangeSource,
  formatChangeUnit,
  formatHubPercentage,
  formatHubPrice,
  resolveHubImpact,
  toEventArrowDirection,
  toRecentPriceChangeEvent,
} from '@sales-portal/domain/pricing/screens/overview/blocs/utils/pricing-hub-formatters';
import { GetPricingHubRecentPriceChangesQuery } from '@/types';
import { PricingHubRecentChangeRow } from '@sales-portal/domain/pricing/screens/overview/types';
import { useMemo } from 'react';

type RecentChange =
  GetPricingHubRecentPriceChangesQuery['getPricingHubRecentPriceChanges'][number];

export const usePricingHubRecentChangesTransformer = (
  changes: RecentChange[] | undefined,
  currencyCode: string,
): PricingHubRecentChangeRow[] =>
  useMemo(
    () =>
      (changes ?? []).map((change, index) => {
        const event = toRecentPriceChangeEvent(change.event);
        const priceDirection = toEventArrowDirection(event);
        const gpImpact = resolveHubImpact(
          change.weekly_gp_impact_in_micros,
          currencyCode,
        );
        return {
          cost: formatHubPrice(change.cost_in_micros, currencyCode),
          customer: change.restaurant_name,
          customerUuid: change.restaurant_id,
          date: formatChangeDate(change.updated_at),
          event,
          // Sliced raw, not timezone-shifted, so the anchor matches the date
          // pricing_impact's own event rows send for the same update.
          eventDate: change.updated_at.slice(0, 10),
          gpPerWeek: gpImpact.text,
          gpPerWeekDirection: gpImpact.direction,
          // A batched write can repeat a pair+timestamp, so the index keeps keys unique.
          id: `${change.variant_pack_id}-${change.restaurant_id}-${change.updated_at}-${index}`,
          item: change.item_name,
          margin: formatHubPercentage(change.gross_profit_percentage),
          // A row with no margin gets no arrow.
          marginDirection:
            change.gross_profit_percentage === null ||
            change.gross_profit_percentage === undefined
              ? undefined
              : priceDirection,
          price: formatHubPrice(change.price_in_micros, currencyCode),
          priceDirection,
          source: formatChangeSource(change.source),
          uom: formatChangeUnit(change.base_unit),
          variantPackUuid: change.variant_pack_id,
        };
      }),
    [changes, currencyCode],
  );
